import React, { useEffect, useMemo, useRef, useState } from "react";
import { Container, Row, Col, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import {
  FiArrowRight,
  FiCheckCircle,
  FiExternalLink,
  FiMapPin,
  FiShield,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import { Trans, useTranslation } from "react-i18next";

import Particle from "../Particle";
import useInViewOnce from "../../hooks/useInViewOnce";

import favrhoodLogo from "../../Assets/Projects/favrhood_logo1.png";
import heroPreview from "../../Assets/favrhoodapp.png";
import boardPreview from "../../Assets/favrhood_board.png";
import favrhoodHelpPreview from "../../Assets/favrhoodhelp.png";
import favrhoodTalentPreview from "../../Assets/favrhoodtalent2.png";
import appPreview from "../../Assets/favrhood_app.png";
import extraPreview from "../../Assets/favrhood.png";

const asArray = (value) => (Array.isArray(value) ? value : []);

const useRepeatReveal = ({ threshold = 0.18 } = {}) => {
  const { ref, inView, hasEntered } = useInViewOnce({ threshold, once: false });
  const prevInViewRef = useRef(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (inView && !prevInViewRef.current) {
      setTick((current) => current + 1);
    }
    prevInViewRef.current = inView;
  }, [inView]);

  return { ref, tick, hasEntered };
};

const RevealPolicyCard = ({ className = "", threshold = 0.18, children }) => {
  const { ref, tick, hasEntered } = useRepeatReveal({ threshold });
  const isPre = !hasEntered && tick === 0;

  return (
    <div
      ref={ref}
      data-anim={tick % 2}
      className={`policy-card favrhood-card favrhood-card-reveal${
        tick > 0 ? " is-anim" : ""
      }${isPre ? " is-pre" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </div>
  );
};

const RevealBox = ({ className = "", threshold = 0.18, children }) => {
  const { ref, tick, hasEntered } = useRepeatReveal({ threshold });
  const isPre = !hasEntered && tick === 0;

  return (
    <div
      ref={ref}
      data-anim={tick % 2}
      className={`favrhood-card favrhood-card-reveal${
        tick > 0 ? " is-anim" : ""
      }${isPre ? " is-pre" : ""}${className ? ` ${className}` : ""}`}
    >
      {children}
    </div>
  );
};

const StepCard = ({ index, title, description }) => {
  const { ref, tick, hasEntered } = useRepeatReveal({ threshold: 0.22 });
  const isPre = !hasEntered && tick === 0;

  return (
    <div
      ref={ref}
      data-anim={tick % 2}
      className={`policy-card favrhood-step favrhood-card favrhood-card-reveal${
        tick > 0 ? " is-anim" : ""
      }${isPre ? " is-pre" : ""}`}
    >
      <div className="favrhood-step-top">
        <span className="favrhood-step-index">{index}</span>
        <h3 className="favrhood-step-title">{title}</h3>
      </div>
      <p className="favrhood-step-desc">{description}</p>
    </div>
  );
};

const AudienceCard = ({ title, bullets, icon }) => {
  const Icon = icon;
  const { ref, tick, hasEntered } = useRepeatReveal({ threshold: 0.22 });
  const isPre = !hasEntered && tick === 0;

  return (
    <div
      ref={ref}
      data-anim={tick % 2}
      className={`policy-card favrhood-audience favrhood-card favrhood-card-reveal${
        tick > 0 ? " is-anim" : ""
      }${isPre ? " is-pre" : ""}`}
    >
      <div className="favrhood-audience-head">
        <span className="favrhood-audience-icon" aria-hidden="true">
          <Icon />
        </span>
        <h3 className="favrhood-audience-title">{title}</h3>
      </div>
      <ul className="favrhood-bullets">
        {bullets.map((bullet) => (
          <li key={bullet}>
            <FiCheckCircle className="favrhood-bullet-icon" aria-hidden="true" />
            <span>{bullet}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ValueCard = ({ title, description, icon }) => {
  const Icon = icon;
  const { ref, tick, hasEntered } = useRepeatReveal({ threshold: 0.22 });
  const isPre = !hasEntered && tick === 0;

  return (
    <div
      ref={ref}
      data-anim={tick % 2}
      className={`policy-card favrhood-value favrhood-card favrhood-card-reveal${
        tick > 0 ? " is-anim" : ""
      }${isPre ? " is-pre" : ""}`}
    >
      <div className="favrhood-value-head">
        <span className="favrhood-value-icon" aria-hidden="true">
          <Icon />
        </span>
        <h3 className="favrhood-value-title">{title}</h3>
      </div>
      <p className="favrhood-value-desc">{description}</p>
    </div>
  );
};

const ScreenshotGallery = ({ screenshots, onOpen, listLabel, openLabel }) => {
  return (
    <div className="favrhood-gallery" role="list" aria-label={listLabel}>
      {screenshots.map((shot) => (
        <div key={shot.id} role="listitem">
          <button
            type="button"
            className="favrhood-gallery-item"
            onClick={() => onOpen(shot)}
            aria-label={openLabel(shot.alt)}
          >
            <img src={shot.src} alt={shot.alt} loading="lazy" />
          </button>
        </div>
      ))}
    </div>
  );
};

function Favrhood() {
  const { t } = useTranslation();

  const sections = [
    { id: "what", label: t("favrhoodPage.sections.what") },
    { id: "gallery", label: t("favrhoodPage.sections.gallery") },
    { id: "account-video", label: t("favrhoodPage.sections.accountVideo") },
    { id: "why", label: t("favrhoodPage.sections.why") },
    { id: "how", label: t("favrhoodPage.sections.how") },
    { id: "for", label: t("favrhoodPage.sections.for") },
    { id: "different", label: t("favrhoodPage.sections.different") },
    { id: "starting", label: t("favrhoodPage.sections.starting") },
    { id: "involved", label: t("favrhoodPage.sections.involved") },
  ];

  const screenshots = [
    { id: "onboarding", src: heroPreview, alt: t("favrhoodPage.gallery.alts.onboarding") },
    { id: "board", src: boardPreview, alt: t("favrhoodPage.gallery.alts.board") },
    { id: "experience", src: appPreview, alt: t("favrhoodPage.gallery.alts.experience") },
    { id: "help", src: favrhoodHelpPreview, alt: t("favrhoodPage.gallery.alts.help") },
    { id: "talent", src: favrhoodTalentPreview, alt: t("favrhoodPage.gallery.alts.talent") },
    { id: "feed", src: extraPreview, alt: t("favrhoodPage.gallery.alts.feed") },
  ];

  const heroChips = asArray(t("favrhoodPage.hero.chips", { returnObjects: true }));
  const glanceItems = asArray(t("favrhoodPage.hero.atAGlance.items", { returnObjects: true }));

  const whyBullets = asArray(t("favrhoodPage.why.bullets", { returnObjects: true }));
  const steps = asArray(t("favrhoodPage.how.steps", { returnObjects: true }));
  const audiences = asArray(t("favrhoodPage.for.audiences", { returnObjects: true }));
  const values = asArray(t("favrhoodPage.different.values", { returnObjects: true }));
  const rolloutBullets = asArray(t("favrhoodPage.starting.bullets", { returnObjects: true }));

  const audienceIconMap = useMemo(
    () => ({
      neighbors: FiMapPin,
      youth: FiZap,
      business: FiTrendingUp,
    }),
    [],
  );

  const valueIconMap = useMemo(
    () => ({
      communityFirst: FiUsers,
      humanCentered: FiShield,
      opportunityFocused: FiZap,
      connectedEcosystem: FiArrowRight,
      hyperlocal: FiMapPin,
      builtToGrow: FiTrendingUp,
    }),
    [],
  );

  const [activeScreenshot, setActiveScreenshot] = useState(null);

  useEffect(() => {
    const updateNavbarOffset = () => {
      const navbarHeight =
        document
          .querySelector(".cf-navbar")
          ?.getBoundingClientRect()
          .height ?? 90;

      document.documentElement.style.setProperty(
        "--cf-navbar-offset",
        `${Math.ceil(navbarHeight)}px`,
      );
    };

    updateNavbarOffset();
    window.addEventListener("resize", updateNavbarOffset, { passive: true });
    return () => window.removeEventListener("resize", updateNavbarOffset);
  }, []);

  const scrollToSection = (event, sectionId) => {
    event.preventDefault();

    const target = document.getElementById(sectionId);
    if (!target) return;

    const cssNavbarOffset = parseFloat(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--cf-navbar-offset")
        .trim(),
    );

    const navbarHeight = Number.isFinite(cssNavbarOffset)
      ? cssNavbarOffset
      : document
          .querySelector(".cf-navbar")
          ?.getBoundingClientRect()
          .height ?? 90;

    const topPadding = 8;

    const y =
      target.getBoundingClientRect().top +
      window.pageYOffset -
      Math.ceil(navbarHeight + topPadding);

    window.history.replaceState(null, "", `#${sectionId}`);
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  const { ref: heroRef, hasEntered: heroSeen } = useInViewOnce({
    threshold: 0.16,
    once: true,
  });
  const { ref: whatRef, hasEntered: whatSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });
  const { ref: whyRef, hasEntered: whySeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });
  const { ref: stepsRef, hasEntered: stepsSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });
  const { ref: audienceRef, hasEntered: audienceSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });
  const { ref: valueRef, hasEntered: valueSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });
  const { ref: rolloutRef, hasEntered: rolloutSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });
  const { ref: ctaRef, hasEntered: ctaSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });

  const { ref: galleryRef, hasEntered: gallerySeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });

  const { ref: videoRef, hasEntered: videoSeen } = useInViewOnce({
    threshold: 0.12,
    once: true,
  });

  return (
    <Container fluid className="about-section favrhood-page">
      <Particle />

      <Container className="favrhood-container">
        <header
          ref={heroRef}
          className={`favrhood-hero favrhood-reveal${heroSeen ? " is-seen" : ""}`}
        >
          <RevealPolicyCard className="favrhood-hero-card" threshold={0.25}>
            <Row className="align-items-center g-4">
              <Col lg={7}>
                <div className="favrhood-kicker">
                  <img
                    src={favrhoodLogo}
                    alt={t("favrhoodPage.a11y.logoAlt")}
                    className="favrhood-logo"
                    loading="lazy"
                  />
                  <span className="favrhood-kicker-text">
                    <Trans
                      i18nKey="favrhoodPage.hero.kicker"
                      components={{ r: <span className="small-r-symbol">®</span> }}
                    />
                  </span>
                </div>

                <h1 className="favrhood-title">{t("favrhoodPage.hero.title")}</h1>
                <p className="favrhood-subtitle">
                  <Trans i18nKey="favrhoodPage.hero.subtitle" components={{ strong: <strong /> }} />
                </p>

                <div className="favrhood-hero-actions">
                  <a
                    className="btn custom-yellow-btn favrhood-primary"
                    href="https://www.favrhood.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("favrhoodPage.hero.actions.visit")}{" "}
                    <FiExternalLink className="favrhood-cta-icon" />
                  </a>
                  <Link
                    className="btn btn-outline-light favrhood-secondary"
                    to="/contact"
                  >
                    {t("favrhoodPage.hero.actions.startCommunity")}{" "}
                    <FiArrowRight className="favrhood-cta-icon" />
                  </Link>
                </div>

                <div className="favrhood-chip-row" aria-label={t("favrhoodPage.a11y.highlights")}> 
                  {heroChips.map((chip) => (
                    <span key={chip} className="favrhood-chip">{chip}</span>
                  ))}
                </div>

                <div className="favrhood-nav favrhood-nav--mobile" aria-label={t("favrhoodPage.a11y.jumpLinks")}>
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      className="favrhood-nav-link"
                      href={`#${section.id}`}
                      onClick={(e) => scrollToSection(e, section.id)}
                    >
                      {section.label}
                    </a>
                  ))}
                </div>
              </Col>

              <Col lg={5} className="d-none d-lg-block">
                <RevealBox className="favrhood-hero-side" threshold={0.25}>
                  <div aria-label={t("favrhoodPage.a11y.atAGlance")}>
                    <h2 className="favrhood-hero-side-title">{t("favrhoodPage.hero.atAGlance.title")}</h2>
                    <ul className="favrhood-hero-side-list">
                      {[
                        { Icon: FiUsers, text: glanceItems[0] },
                        { Icon: FiZap, text: glanceItems[1] },
                        { Icon: FiShield, text: glanceItems[2] },
                      ]
                        .filter((item) => Boolean(item.text))
                        .map(({ Icon, text }) => (
                          <li key={text} className="favrhood-hero-side-item">
                            <Icon aria-hidden="true" />
                            <span>{text}</span>
                          </li>
                        ))}
                    </ul>

                    <a className="btn btn-outline-light favrhood-hero-side-btn" href="#gallery">
                      {t("favrhoodPage.hero.atAGlance.cta")}{" "}
                      <FiArrowRight className="favrhood-cta-icon" />
                    </a>
                  </div>
                </RevealBox>
              </Col>
            </Row>
          </RevealPolicyCard>
        </header>

        <Row className="g-4 favrhood-body">
          <Col lg={8}>
            <section
              id="what"
              ref={whatRef}
              className={`favrhood-section favrhood-reveal${whatSeen ? " is-seen" : ""}`}
            >
              <h2 className="about-section-heading">{t("favrhoodPage.what.heading")}</h2>
              <RevealPolicyCard className="favrhood-copy">
                <p className="favrhood-paragraph">
                  {t("favrhoodPage.what.p1")}
                </p>
                <p className="favrhood-paragraph">
                  {t("favrhoodPage.what.p2")}
                </p>
                <p className="favrhood-paragraph favrhood-paragraph--muted">
                  {t("favrhoodPage.what.p3")}
                </p>
              </RevealPolicyCard>
            </section>

            <section
              id="gallery"
              ref={galleryRef}
              className={`favrhood-section favrhood-reveal${gallerySeen ? " is-seen" : ""}`}
              aria-label={t("favrhoodPage.a11y.gallery")}
            >
              <h2 className="about-section-heading">{t("favrhoodPage.gallery.heading")}</h2>
              <p className="favrhood-sublead">
                {t("favrhoodPage.gallery.lead")}
              </p>
              <RevealPolicyCard className="favrhood-gallery-card">
                <ScreenshotGallery
                  screenshots={screenshots}
                  onOpen={(shot) => setActiveScreenshot(shot)}
                  listLabel={t("favrhoodPage.a11y.gallery")}
                  openLabel={(alt) => t("favrhoodPage.a11y.openScreenshot", { alt })}
                />
              </RevealPolicyCard>
            </section>

            <section
              id="account-video"
              ref={videoRef}
              className={`favrhood-section favrhood-reveal${videoSeen ? " is-seen" : ""}`}
              aria-label={t("favrhoodPage.a11y.accountVideo")}
            >
              <h2 className="about-section-heading">{t("favrhoodPage.accountVideo.heading")}</h2>
              <p className="favrhood-sublead">
                {t("favrhoodPage.accountVideo.lead")}
              </p>
              <RevealPolicyCard className="favrhood-video-card">
                <div className="favrhood-video-frame">
                  <iframe
                    src="https://www.youtube-nocookie.com/embed/-L8TsjDLBRs"
                    title={t("favrhoodPage.accountVideo.iframeTitle")}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </div>
              </RevealPolicyCard>
            </section>

            <section
              id="why"
              ref={whyRef}
              className={`favrhood-section favrhood-reveal${whySeen ? " is-seen" : ""}`}
            >
              <h2 className="about-section-heading">{t("favrhoodPage.why.heading")}</h2>
              <RevealPolicyCard className="favrhood-copy">
                <p className="favrhood-paragraph">
                  <Trans
                    i18nKey="favrhoodPage.why.intro"
                    components={{ r: <span className="small-r-symbol">®</span> }}
                  />
                </p>

                <ul className="favrhood-bullets favrhood-bullets--tight">
                  {whyBullets.map((bullet) => (
                    <li key={bullet}>
                      <FiCheckCircle className="favrhood-bullet-icon" aria-hidden="true" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>

                <p className="favrhood-paragraph favrhood-paragraph--muted">
                  {t("favrhoodPage.why.outro")}
                </p>
              </RevealPolicyCard>
            </section>

            <section
              id="how"
              ref={stepsRef}
              className={`favrhood-section favrhood-reveal${stepsSeen ? " is-seen" : ""}`}
              aria-label={t("favrhoodPage.a11y.howItWorks")}
            >
              <h2 className="about-section-heading">{t("favrhoodPage.how.heading")}</h2>
              <p className="favrhood-sublead">
                {t("favrhoodPage.how.lead")}
              </p>

              <div className="favrhood-grid favrhood-grid--steps">
                {steps.map((step, index) => (
                  <StepCard
                    key={step.key || step.title || String(index)}
                    index={String(index + 1)}
                    title={step.title}
                    description={step.description}
                  />
                ))}
              </div>
            </section>

        <section
          id="for"
          ref={audienceRef}
          className={`favrhood-section favrhood-reveal${audienceSeen ? " is-seen" : ""}`}
          aria-label={t("favrhoodPage.a11y.whoItsFor")}
        >
          <h2 className="about-section-heading">{t("favrhoodPage.for.heading")}</h2>

          <div className="favrhood-grid favrhood-grid--audience">
            {audiences.map((audience) => (
              <AudienceCard
                key={audience.key || audience.title}
                title={audience.title}
                icon={audienceIconMap[audience.key] || FiUsers}
                bullets={asArray(audience.bullets)}
              />
            ))}
          </div>
        </section>

        <section
          id="different"
          ref={valueRef}
          className={`favrhood-section favrhood-reveal${valueSeen ? " is-seen" : ""}`}
          aria-label={t("favrhoodPage.a11y.whatsDifferent")}
        >
          <h2 className="about-section-heading">{t("favrhoodPage.different.heading")}</h2>

          <div className="favrhood-grid favrhood-grid--values">
            {values.map((value) => (
              <ValueCard
                key={value.key || value.title}
                title={value.title}
                icon={valueIconMap[value.key] || FiArrowRight}
                description={value.description}
              />
            ))}
          </div>

          <RevealPolicyCard className="favrhood-powered">
            <h3 className="favrhood-powered-title">
              <Trans
                i18nKey="favrhoodPage.different.powered.title"
                components={{ r: <span className="small-r-symbol">®</span> }}
              />
            </h3>
            <p className="favrhood-powered-desc">
              <Trans
                i18nKey="favrhoodPage.different.powered.desc"
                components={{ r: <span className="small-r-symbol">®</span> }}
              />
            </p>
          </RevealPolicyCard>
        </section>

        <section
          id="starting"
          ref={rolloutRef}
          className={`favrhood-section favrhood-reveal${rolloutSeen ? " is-seen" : ""}`}
          aria-label={t("favrhoodPage.a11y.whereStarting")}
        >
          <h2 className="about-section-heading">{t("favrhoodPage.starting.heading")}</h2>
          <RevealPolicyCard className="favrhood-copy">
            <p className="favrhood-paragraph">
              {t("favrhoodPage.starting.intro")}
            </p>
            <ul className="favrhood-bullets favrhood-bullets--tight">
              {rolloutBullets.map((bullet) => (
                <li key={bullet}>
                  <FiCheckCircle className="favrhood-bullet-icon" aria-hidden="true" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>

            <div className="favrhood-callout">
              <span className="favrhood-callout-title">{t("favrhoodPage.starting.callout.label")}</span>
              <span className="favrhood-callout-text">
                {t("favrhoodPage.starting.callout.text")}
              </span>
            </div>
          </RevealPolicyCard>
        </section>

        <section
          id="involved"
          ref={ctaRef}
          className={`favrhood-section favrhood-reveal${ctaSeen ? " is-seen" : ""}`}
          aria-label={t("favrhoodPage.a11y.getInvolved")}
        >
          <h2 className="about-section-heading">{t("favrhoodPage.involved.heading")}</h2>

          <div className="favrhood-grid favrhood-grid--cta">
            <RevealPolicyCard className="favrhood-cta" threshold={0.2}>
              <h3 className="favrhood-cta-title">{t("favrhoodPage.involved.cards.join.title")}</h3>
              <p className="favrhood-cta-desc">{t("favrhoodPage.involved.cards.join.desc")}</p>
              <a
                className="btn custom-yellow-btn favrhood-cta-btn"
                href="https://www.favrhood.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("favrhoodPage.involved.cards.join.button")}{" "}
                <FiExternalLink className="favrhood-cta-icon" />
              </a>
            </RevealPolicyCard>

            <RevealPolicyCard className="favrhood-cta" threshold={0.2}>
              <h3 className="favrhood-cta-title">{t("favrhoodPage.involved.cards.start.title")}</h3>
              <p className="favrhood-cta-desc">
                {t("favrhoodPage.involved.cards.start.desc")}
              </p>
              <Link className="btn custom-yellow-btn favrhood-cta-btn" to="/contact">
                {t("favrhoodPage.involved.cards.start.button")}{" "}
                <FiArrowRight className="favrhood-cta-icon" />
              </Link>
            </RevealPolicyCard>

            <RevealPolicyCard className="favrhood-cta" threshold={0.2}>
              <h3 className="favrhood-cta-title">{t("favrhoodPage.involved.cards.partner.title")}</h3>
              <p className="favrhood-cta-desc">
                {t("favrhoodPage.involved.cards.partner.desc")}
              </p>
              <Link className="btn custom-yellow-btn favrhood-cta-btn" to="/about/our-partners">
                {t("favrhoodPage.involved.cards.partner.button")}{" "}
                <FiArrowRight className="favrhood-cta-icon" />
              </Link>
            </RevealPolicyCard>
          </div>

          <RevealPolicyCard className="favrhood-vision" threshold={0.16}>
            <h3 className="favrhood-vision-title">{t("favrhoodPage.involved.vision.title")}</h3>
            <p className="favrhood-vision-desc">
              {t("favrhoodPage.involved.vision.desc")}
            </p>
          </RevealPolicyCard>
        </section>
          </Col>

          <Col lg={4} className="d-none d-lg-block">
            <aside className="favrhood-aside">
              <RevealPolicyCard className="favrhood-aside-card" threshold={0.12}>
                <h3 className="favrhood-aside-title">{t("favrhoodPage.sidebar.onThisPage")}</h3>
                <nav className="favrhood-aside-nav">
                  {sections.map((section) => (
                    <a
                      key={section.id}
                      className="favrhood-aside-link"
                      href={`#${section.id}`}
                      onClick={(e) => scrollToSection(e, section.id)}
                    >
                      {section.label}
                    </a>
                  ))}
                </nav>
              </RevealPolicyCard>

              <RevealPolicyCard className="favrhood-aside-card" threshold={0.12}>
                <h3 className="favrhood-aside-title">{t("favrhoodPage.sidebar.quickActions")}</h3>
                <div className="favrhood-aside-actions">
                  <a
                    className="btn custom-yellow-btn favrhood-aside-btn"
                    href="https://www.favrhood.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("favrhoodPage.sidebar.createProfile")}{" "}
                    <FiExternalLink className="favrhood-cta-icon" />
                  </a>
                  <Link className="btn btn-outline-light favrhood-aside-btn" to="/contact">
                    {t("favrhoodPage.sidebar.bringToYou")}{" "}
                    <FiArrowRight className="favrhood-cta-icon" />
                  </Link>
                  <Link
                    className="btn btn-outline-light favrhood-aside-btn"
                    to="/about/our-partners"
                  >
                    {t("favrhoodPage.sidebar.partnerWithUs")}{" "}
                    <FiArrowRight className="favrhood-cta-icon" />
                  </Link>
                </div>
              </RevealPolicyCard>
            </aside>
          </Col>
        </Row>

        <Modal
          show={Boolean(activeScreenshot)}
          onHide={() => setActiveScreenshot(null)}
          centered
          size="lg"
          contentClassName="favrhood-lightbox"
        >
          <Modal.Header closeButton className="favrhood-lightbox-header" />
          <Modal.Body className="favrhood-lightbox-body">
            {activeScreenshot ? (
              <img
                className="favrhood-lightbox-img"
                src={activeScreenshot.src}
                alt={activeScreenshot.alt}
              />
            ) : null}
          </Modal.Body>
        </Modal>
      </Container>
    </Container>
  );
}

export default Favrhood;
